import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  CircularProgress,
  Typography
} from '@mui/material';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TopBar from '../../../../components/utils/TopBar';

import {
  GetSubscriptionsOverviewBIZsubs
} from '../../../../redux/actions/business/subscriptions/subscriptionsOverviewActions';

const SECTION_LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

const Section = ({ title, hint, children }) => (
  <Box mb={"22px"}>
    <Typography variant='subtitle2' sx={{ mb: hint ? "2px" : "10px" }}>
      {title}
    </Typography>
    {
      hint
      &&
      <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "10px" }}>
        {hint}
      </Typography>
    }
    {children}
  </Box>
);

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const overview = useSelector(state => state["business.subscriptions"]?.subscriptionsOverview);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleGetOverview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleGetOverview() {
    await dispatch(GetSubscriptionsOverviewBIZsubs(setLoading));
  }

  const catalogue = overview?.catalogue;
  const adoption = overview?.adoption;
  const companyConfig = overview?.company_config;
  const integrity = overview?.integrity;
  const planAdoption = overview?.plan_adoption || [];
  const planType = overview?.plan_type || [];
  const pricingShape = overview?.pricing_shape || [];

  // Only rows that are both actionable and non-zero — a wall of green zeroes
  // would just be noise.
  const attention = [
    {
      count: integrity?.companies_without_plan,
      one: "company has no subscription plan assigned",
      many: "companies have no subscription plan assigned",
      to: "/business/identity/companies",
    },
    {
      count: integrity?.active_plans_without_services,
      one: "active plan has no services attached, so it prices nothing",
      many: "active plans have no services attached, so they price nothing",
      to: "/business/subscriptions/services",
    },
    {
      count: integrity?.services_without_plan,
      one: "service points at a plan that no longer resolves",
      many: "services point at a plan that no longer resolves",
      to: "/business/subscriptions/services",
    },
    {
      count: integrity?.services_without_product,
      one: "service points at a product that no longer resolves",
      many: "services point at a product that no longer resolves",
      to: "/business/subscriptions/services",
    },
    {
      count: integrity?.services_without_delivery_method,
      one: "service points at a delivery method that no longer resolves",
      many: "services point at a delivery method that no longer resolves",
      to: "/business/subscriptions/services",
    },
    {
      count: integrity?.company_addons_without_addon,
      one: "company addon points at an addon that no longer resolves",
      many: "company addons point at an addon that no longer resolves",
      to: "/business/subscriptions/company-plan-addons",
    },
    {
      count: integrity?.exclusions_without_service,
      one: "service exclusion points at a service that no longer resolves",
      many: "service exclusions point at a service that no longer resolves",
      to: "/business/subscriptions/disallowed-services",
    },
  ]
    .filter((item) => Number(item.count) > 0)
    .map((item) => ({
      ...item,
      label: Number(item.count) === 1 ? item.one : item.many,
    }));

  const adoptionTiles = [
    { label: "Companies on a plan", value: adoption?.companies_on_plan ?? 0, accent: TILE_ACCENT.good, onClick: () => navigate("/business/identity/companies") },
    { label: "No plan assigned", value: adoption?.companies_without_plan ?? 0, accent: Number(adoption?.companies_without_plan) > 0 ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/identity/companies") },
    { label: "Plan change scheduled", value: adoption?.plan_change_scheduled ?? 0, accent: TILE_ACCENT.info, onClick: () => navigate("/business/identity/companies") },
  ];

  const catalogueTiles = [
    { label: "Products", value: `${catalogue?.products_active ?? 0}/${catalogue?.products ?? 0}`, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/products") },
    { label: "Plans", value: `${catalogue?.plans_active ?? 0}/${catalogue?.plans ?? 0}`, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/subscription-plans") },
    { label: "Services", value: `${catalogue?.services_active ?? 0}/${catalogue?.services ?? 0}`, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/services") },
    { label: "Plan addons", value: `${catalogue?.addons_active ?? 0}/${catalogue?.addons ?? 0}`, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/plan-addons") },
    { label: "Delivery methods", value: catalogue?.delivery_methods ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/delivery-methods") },
    { label: "Integrations", value: catalogue?.integrations ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/integrations") },
  ];

  const overrideTiles = [
    { label: "Companies with addons", value: companyConfig?.companies_with_addons ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/company-plan-addons") },
    { label: "Addon rows", value: companyConfig?.addon_rows ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/company-plan-addons") },
    { label: "Companies with exclusions", value: companyConfig?.companies_with_exclusions ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/disallowed-services") },
    { label: "Exclusion rows", value: companyConfig?.exclusion_rows ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/subscriptions/disallowed-services") },
  ];

  // A single-valued breakdown says nothing the totals don't, so it only earns
  // its place once the column actually varies.
  const showPlanType = planType.length > 1;
  const showPricingShape = pricingShape.length > 1;

  const plansVisible = catalogue?.plans_visible ?? 0;

  return (
    <Box>

      <TopBar
        title={"Products & Subscriptions"}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetOverview();
        }}
        />

      <Typography
        variant='caption'
        sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "20px" }}>
        Live counts for the plan catalogue and who is on it. Tiles link to the
        screen they came from. Counts exclude soft-deleted rows.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        !overview
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <Typography>Overview unavailable</Typography>
        </Box>
        :
        <Box>

          {/* ---------- needs attention ---------- */}
          <Section title={"Needs attention"}>
            {
              attention.length === 0
              ?
              <Box
                className="flexCenter_Row"
                gap={"10px"}
                sx={{
                  border: `1px solid ${TILE_ACCENT.good}40`,
                  background: `${TILE_ACCENT.good}14`,
                  borderRadius: "12px",
                  padding: "14px 16px",
                }}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
                <Typography variant='body2'>
                  Nothing to action — every company has a plan, and every plan,
                  service and addon reference resolves.
                </Typography>
              </Box>
              :
              <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
                {
                  attention.map((item, index) => (
                    <Box
                      key={index}
                      className="flexCenterSBRow"
                      onClick={() => navigate(item.to)}
                      sx={{
                        border: `1px solid ${TILE_ACCENT.warn}40`,
                        background: `${TILE_ACCENT.warn}14`,
                        borderRadius: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": {
                          background: `${TILE_ACCENT.warn}24`,
                          borderColor: `${TILE_ACCENT.warn}80`,
                        },
                      }}>
                      <Box className="flexCenter_Row" gap={"12px"}>
                        <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.warn }} />
                        <Typography variant='body2'>
                          <b>{item.count}</b>&nbsp;{item.label}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: SECTION_LABEL_COLOR }} />
                    </Box>
                  ))
                }
              </Box>
            }
          </Section>

          {/* ---------- adoption ---------- */}
          <Section
            title={"Plan adoption"}
            hint={"A company with no plan cannot be billed."}>
            <SummaryTiles tiles={adoptionTiles} />

            {
              planAdoption.length > 0
              &&
              <Box display={"flex"} flexDirection={"column"} gap={"6px"} mt={"4px"}>
                {
                  planAdoption.map((plan) => (
                    <Box
                      key={plan.id}
                      className="flexCenterSBRow"
                      onClick={() => navigate("/business/subscriptions/subscription-plans")}
                      sx={{
                        border: "1px solid rgba(44, 45, 47, 1)",
                        background: "rgba(44, 45, 47, 0.44)",
                        borderRadius: "10px",
                        padding: "10px 16px",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": { background: "rgba(44, 45, 47, 0.8)" },
                      }}>
                      <Box className="flexCenter_Row" gap={"10px"} flexWrap={"wrap"}>
                        <Typography variant='body2'>
                          {plan.plan_name || "(unnamed plan)"}
                        </Typography>
                        {
                          !plan.is_active
                          &&
                          <Chip size='small' label="inactive" color='warning' variant='outlined' />
                        }
                        {
                          plan.is_active && !plan.is_visible
                          &&
                          <Chip size='small' label="hidden" color='info' variant='outlined' />
                        }
                      </Box>
                      <Typography variant='body2' sx={{ color: SECTION_LABEL_COLOR, whiteSpace: "nowrap" }}>
                        {plan.companies} {Number(plan.companies) === 1 ? "company" : "companies"}
                      </Typography>
                    </Box>
                  ))
                }
              </Box>
            }

            {
              showPlanType
              &&
              <Box mt={"12px"}>
                <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "8px" }}>
                  Billing cycle
                </Typography>
                <Box className="flexCenter_Row" gap={"8px"} flexWrap={"wrap"}>
                  {
                    planType.map((p, i) => (
                      <Chip key={i} size='small' variant='outlined'
                        label={`${p.plan_type} · ${p.count}`} />
                    ))
                  }
                </Box>
              </Box>
            }
          </Section>

          {/* ---------- catalogue ---------- */}
          <Section
            title={"Catalogue"}
            hint={`Active / total. ${plansVisible} ${plansVisible === 1 ? "plan is" : "plans are"} visible to customers.`}>
            <SummaryTiles tiles={catalogueTiles} />

            {
              showPricingShape
              &&
              <Box mt={"4px"}>
                <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "8px" }}>
                  How services charge
                </Typography>
                <Box className="flexCenter_Row" gap={"8px"} flexWrap={"wrap"}>
                  {
                    pricingShape.map((p, i) => (
                      <Chip key={i} size='small' variant='outlined' color='primary'
                        label={`${p.fee_type} · ${p.who_pays} · ${p.count}`} />
                    ))
                  }
                </Box>
              </Box>
            }
          </Section>

          {/* ---------- per-company overrides ---------- */}
          <Section
            title={"Per-company overrides"}
            hint={"Addons bought on top of a plan, and services switched off for a company."}>
            <SummaryTiles tiles={overrideTiles} />
          </Section>
        </Box>
      }
    </Box>
  )
}

export default HomePage;
